/**
 * Number to Words Utility
 *
 * Converts numbers to English words for natural text-to-speech pronunciation.
 * Examples:
 *   4005 → "four thousand and five"
 *   3155 → "three thousand one hundred and fifty-five"
 *   10000 → "ten thousand"
 */

const ONES = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
] as const

const TENS = [
  '',
  '',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
] as const

const SCALE = ['', 'thousand', 'million', 'billion'] as const

/**
 * Convert a number (0-999) to words
 */
function convertHundreds(num: number): string {
  if (num === 0) return ''

  let result = ''

  // Handle hundreds
  if (num >= 100) {
    result += ONES[Math.floor(num / 100)] + ' hundred'
    num %= 100
    if (num > 0) {
      result += ' and '
    }
  }

  // Handle tens and ones
  if (num >= 20) {
    result += TENS[Math.floor(num / 10)]
    const ones = num % 10
    if (ones > 0) {
      result += '-' + ONES[ones]
    }
  } else if (num > 0) {
    result += ONES[num]
  }

  return result
}

/**
 * Convert a number to English words
 *
 * @param num - The number to convert (supports up to billions)
 * @returns The number as English words
 *
 * @example
 * numberToWords(4005) // "four thousand and five"
 * numberToWords(3155) // "three thousand one hundred and fifty-five"
 * numberToWords(10000) // "ten thousand"
 * numberToWords(0) // "zero"
 * numberToWords(-500) // "negative five hundred"
 */
export function numberToWords(num: number): string {
  // Handle zero
  if (num === 0) return 'zero'

  // Handle negative numbers
  if (num < 0) {
    return 'negative ' + numberToWords(Math.abs(num))
  }

  // Round to integer
  num = Math.round(num)

  const parts: string[] = []
  let scaleIndex = 0

  // Process each group of three digits
  while (num > 0) {
    const chunk = num % 1000

    if (chunk > 0) {
      const chunkWords = convertHundreds(chunk)
      const scale = SCALE[scaleIndex]

      if (scale) {
        parts.unshift(chunkWords + ' ' + scale)
      } else {
        parts.unshift(chunkWords)
      }
    }

    num = Math.floor(num / 1000)
    scaleIndex++
  }

  // Join parts with appropriate connectors
  let result = ''

  for (let i = 0; i < parts.length; i++) {
    if (i === 0) {
      result = parts[i]
    } else if (i === parts.length - 1) {
      // Check if last part is less than 100 (needs "and")
      const lastChunk = parts[i]
      const needsAnd = !lastChunk.includes('hundred')
      result += (needsAnd ? ' and ' : ' ') + lastChunk
    } else {
      result += ' ' + parts[i]
    }
  }

  return result
}

/**
 * Format a point value for natural TTS pronunciation
 *
 * @param points - The point value to format
 * @param includeUnit - Whether to append "points" (default: true)
 * @returns Formatted string for TTS
 *
 * @example
 * formatPointsForTTS(4005) // "four thousand and five points"
 * formatPointsForTTS(500, false) // "five hundred"
 * formatPointsForTTS(-55) // "negative fifty-five points"
 */
export function formatPointsForTTS(points: number, includeUnit = true): string {
  const words = numberToWords(points)
  return includeUnit ? `${words} points` : words
}

/**
 * Format a penalty value for TTS (always speaks absolute value with "penalty" context)
 *
 * @param penalty - The penalty value (typically negative)
 * @returns Formatted string for TTS
 *
 * @example
 * formatPenaltyForTTS(-55) // "fifty-five points in penalties"
 * formatPenaltyForTTS(244) // "two hundred and forty-four points in penalties"
 */
export function formatPenaltyForTTS(penalty: number): string {
  const absoluteValue = Math.abs(penalty)
  const words = numberToWords(absoluteValue)
  return `${words} points in penalties`
}

/**
 * Format a score comparison for TTS
 *
 * @param teamName - The team name
 * @param teamPenalty - The team's penalty (typically negative)
 * @param opponentPenalty - The opponent's penalty (typically negative)
 * @returns Formatted string for TTS
 *
 * @example
 * formatTeamPenaltiesForTTS("Your team", -55, -244)
 * // "Your team had fifty-five points in penalties. Opponents had two hundred and forty-four."
 */
export function formatTeamPenaltiesForTTS(
  teamName: string,
  teamPenalty: number,
  opponentPenalty: number
): string {
  const teamWords = numberToWords(Math.abs(teamPenalty))
  const opponentWords = numberToWords(Math.abs(opponentPenalty))

  return `${teamName} had ${teamWords} points in penalties. Opponents had ${opponentWords}.`
}
